import React from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { PopupSelect } from '@atlaskit/select';
import {
	getOptionKey,
	RESULTS_SORT_FIELDS,
	type ResultsSortOptionKey,
	ResultsSortOptions,
	SORT_FIELD_KEY,
} from '@atlassian/search-client';

import { ProductKeys } from '../../../../../common/constants/products';
import { FilterButton } from '../../../../../common/ui/filters/filter-button';
import { FilterButtonOld } from '../../../../../common/ui/filters/filter-button-old';
import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter, {
	type SelectFilterOption,
} from '../../../../../common/ui/filters/select-filter';
import { matchReferenceWidthModifier } from '../../../../../common/ui/filters/utils/popper';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
} from '../../../../../controllers/store/bootstrap';
import { useResultsSort, useSelectedProduct } from '../../../../../controllers/store/search';
import { filter as sortFilter } from '../../../../../controllers/store/search/filters/sort';

import { messages } from './messages';

type ResultsSortProps = {
	isFilterInDropdown?: boolean;
	matchReferenceWidth?: boolean;
};

const ResultsSortOld = ({
	isFilterInDropdown,
	matchReferenceWidth = isFilterInDropdown,
}: ResultsSortProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [{ order, field }, { setSortField, setSortOrder }] = useResultsSort();
	const filtersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const handleSelectOption = (option: { label: string; value: ResultsSortOptionKey }) => {
		setSortField(RESULTS_SORT_FIELDS[option.value].field);
		setSortOrder(RESULTS_SORT_FIELDS[option.value].order);
	};

	if (
		!(
			selectedProduct === ProductKeys.Confluence ||
			(selectedProduct === ProductKeys.Jira && fg('rovo_fps_jira_sort_results'))
		)
	) {
		return null;
	}

	const selectedOptionKey = getOptionKey(field, order);

	const options = ResultsSortOptions.map((option) => ({
		label: formatMessage(messages[option.labelKey]),
		value: option.labelKey,
		isSelected: option.labelKey === selectedOptionKey,
	}));

	return (
		<PopupSelect
			label={formatMessage(messages.sortOptionsLabel)}
			blurInputOnSelect={false}
			tabSelectsValue={false}
			backspaceRemovesValue={false}
			hideSelectedOptions={false}
			controlShouldRenderValue={false}
			isClearable={false}
			maxMenuWidth="22rem"
			maxMenuHeight={400}
			menuPlacement="bottom"
			isOptionSelected={(option) => option.isSelected}
			onChange={(value) => value && handleSelectOption(value)}
			options={options}
			popperProps={{
				strategy: 'fixed',
				modifiers: [
					{ name: 'offset', options: { offset: [0, 8] } },
					{
						name: 'preventOverflow',
						enabled: false,
					},
					...(matchReferenceWidth ? [matchReferenceWidthModifier] : []),
				],
			}}
			target={({ isOpen, ...triggerProps }) => {
				const isSelected = isOpen || field !== SORT_FIELD_KEY.RELEVANCE;
				return filtersDiscoverability2Enabled ? (
					<FilterButton
						{...triggerProps}
						isSelected={isSelected}
						testId="results-sort-button"
						filterOrControlId="sort"
						isFilterInDropdown={isFilterInDropdown}
					>
						{formatMessage(messages[RESULTS_SORT_FIELDS[selectedOptionKey].selectedLabelKey])}
					</FilterButton>
				) : (
					<FilterButtonOld
						isSelected={isSelected}
						otherProps={triggerProps}
						label={formatMessage(messages[RESULTS_SORT_FIELDS[selectedOptionKey].selectedLabelKey])}
						filterOrControlId={'sort'}
					/>
				);
			}}
		/>
	);
};

const ResultsSortNew = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps) => {
	const { formatMessage } = useIntl();

	const [primaryProduct] = usePrimaryProduct();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [selectedProduct] = useSelectedProduct();

	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: sortFilter.id,
	});

	// Do not show if the select product is not Confluence or Jira
	if (
		!(
			selectedProduct === ProductKeys.Confluence ||
			(selectedProduct === ProductKeys.Jira && fg('rovo_fps_jira_sort_results'))
		)
	) {
		return null;
	}

	if (!filterData) {
		return null;
	}

	const { isLoading, selectedOptions, options } = filterData;

	const selectedLabelKey = selectedOptionToSelectedLabelKey(selectedOptions[0]);
	const label = selectedLabelKey
		? formatMessage(messages[selectedLabelKey])
		: formatMessage(messages[RESULTS_SORT_FIELDS.relevanceDESC.selectedLabelKey]);

	return (
		<SelectFilter
			contentFit={fit}
			onDropdownOpen={loadInitialOptions}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={options}
			selectedOptions={selectedOptions}
			label={label}
			filterOrControlId={sortFilter.id}
			isLoading={isLoading}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: sortFilter.id });
			}}
			getFirstSelectedLabel={() => ''}
		/>
	);
};

export const ResultsSort = (props: FilterProps) => {
	return fg('rovo_search_page_sort_filter_in_store') ? (
		<ResultsSortNew {...props} />
	) : (
		<ResultsSortOld {...props} />
	);
};

const selectedOptionToSelectedLabelKey = (option: SelectFilterOption) => {
	switch (option?.value) {
		case 'relevance':
			return RESULTS_SORT_FIELDS.relevanceDESC.selectedLabelKey;
		case 'updatedNewest':
			return RESULTS_SORT_FIELDS.latestModifiedDateDESC.selectedLabelKey;
		case 'updatedOldest':
			return RESULTS_SORT_FIELDS.latestModifiedDateASC.selectedLabelKey;
		case 'createdNewest':
			return RESULTS_SORT_FIELDS.createdDateDESC.selectedLabelKey;
		case 'createdOldest':
			return RESULTS_SORT_FIELDS.createdDateASC.selectedLabelKey;
	}
};
